import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { SISCard, SISCallout, SISInfluence, SISTakeaway, SISNoteImage } from "@/components/sis/SISLessonShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";

export default function SouthIndian4() {
  const { lesson, course, accent } = findLesson("south-indian-songbook", "4");
  return (
    <LessonShell
      course={course}
      courseSlug="south-indian-songbook"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <YouTubeEmbed
        videoId="xfiGXEr-VmY"
        title="Kaatrai Konjam — Ilaiyaraaja · Neethaane En Ponvasantham (2012)"
        caption="Tap your knee and try to count in 7: ONE-two-three-four, FIVE-six-seven. Can you feel where it groups?"
      />

      <SISCard label="The big idea" title="Counting to 7 — and why it matters">
        <p>
          Most Western pop music lives in 4/4. Carnatic music uses many cycles, including 3, 5,
          7 and 9 beats. Kaatrai Konjam, a film song in E major, is in <strong>7/8</strong> —
          seven eighth notes per cycle. Not seven quarter notes. Seven eighth notes, which Jason's
          chart beams as 4+3.
        </p>
        <p>
          Even Jason found it hard at first: <em>"To count that seven took some time."</em> It
          is a physical task that most pianists trained in Western music will find genuinely
          difficult. Your body has been conditioned to expect the downbeat every 4 or 8 beats. In
          7, the downbeat returns every 7 — which means it lands on a different part of the bar
          each time relative to a 4/4 grid.
        </p>
        <p>
          And yet the song does not sound "difficult". Jason hears it as <em>"almost like a
          gospel 7 by 8 song"</em> — warm, singable harmony that makes the odd meter feel natural.
        </p>
      </SISCard>

      <SISCard label="How to count 7/8" title="Feeling the groupings: 3+2+2 and 4+3">
        <p>
          The key to odd meters is not counting to 7 as if it were a single unit. It is finding
          the <strong>internal groupings</strong> that give the cycle its feel. For 7/8, there are
          two common groupings:
        </p>
        <ul className="ml-5 list-disc space-y-2">
          <li>
            <strong>3+2+2:</strong> ONE-and-TWO — ONE-TWO — ONE-TWO. Feel the first group as
            slightly longer (three eighth notes), then two pairs. This gives a limping, almost
            syncopated feel — like a waltz that catches its breath.
          </li>
          <li>
            <strong>4+3:</strong> ONE-and-TWO-and — ONE-and-TWO. The first group is four eighth
            notes (like a standard 4/4 half-bar), followed by a three (like a fast waltz). This
            sounds more march-like, more forward-propelling.
          </li>
        </ul>
        <p>
          Jason's chart beams the bar of Kaatrai Konjam as 4+3, and he notes that the song has
          several different 7-beat sections. Try both groupings against the recording and feel
          which one each section leans on.
        </p>
      </SISCard>

      <RomanNumeralAnalysis
        label="E major — the three main chords (from Jason's chart)"
        caption="Jason predicts the chords straight from the vocal line: an A in the melody suggests A major, a G♯ suggests E major, an F♯ suggests B major. I, IV and V of E major — with the plagal move IV → I (A → E), the 'Amen' cadence of gospel."
        chords={[
          { chord: "E", rn: "I",  function: "Tonic",        pitches: ["E3", "G#3", "B3"] },
          { chord: "A", rn: "IV", function: "Pre-dominant", pitches: ["A3", "C#4", "E4"] },
          { chord: "E", rn: "I",  function: "Tonic",        pitches: ["E3", "G#3", "B3"] },
          { bar: true },
          { chord: "B", rn: "V",  function: "Dominant",     pitches: ["B2", "D#3", "F#3"] },
          { chord: "E", rn: "I",  function: "Tonic",        pitches: ["E3", "G#3", "B3"] },
        ]}
      />

      <SISCard label="The gospel harmony" title="IV → I: the plagal cadence (the 'Amen')">
        <p>
          Western classical cadences usually resolve through the <strong>dominant</strong>:
          V → I (the "authentic cadence"). Gospel music often prefers the <strong>plagal cadence</strong>: IV → I. If you have ever heard a
          congregation sing "A-men" at the end of a hymn, you have heard this. The IV chord has
          a softer, more enveloping quality than the V. It does not demand resolution — it offers it.
        </p>
        <p>
          In E major, the plagal cadence is A → E. A contains A, C♯ and E. When it resolves to
          E, the A falls a half-step to G♯, the C♯ falls a step to B, and the E stays put. That
          sustained E is the common tone — the one that says we have arrived. The minor version,
          Am → E (iv → I), turns up in this song's line cliché below; Jason calls it the minor
          plagal cadence.
        </p>
        <p>
          The plagal cadences are a big part of why Jason hears this song as gospel: <em>"I would
          say this is a very gospel song."</em> Even in a 7/8 cycle, the ear recognises the
          IV → I and feels the resolution as settled and complete.
        </p>
      </SISCard>

      <SISCard label="The line cliché" title="Descending chromatic bass under a static chord">
        <p>
          One of the most recognisable textures in both gospel and classical music is the
          <strong> line cliché</strong>: a line — often the bass — moving by step, mostly
          chromatically, while the harmony above it shifts around it. Jason marks one at 1:17 in
          the song:
        </p>
        <ul className="ml-5 list-disc space-y-1.5">
          <li><strong>E</strong> — bass: E</li>
          <li><strong>Bm/D</strong> — bass: D (a whole step down)</li>
          <li><strong>Amaj7/C♯</strong> — bass: C♯ (a half step down)</li>
          <li><strong>Am/C</strong> — bass: C (a half step down)</li>
          <li><strong>E/B</strong> — bass: B (a half step down)</li>
          <li><strong>B</strong></li>
        </ul>
        <p>
          Bm and Am are not in the E major scale — they are borrowed chords — yet the passage
          works because, as Jason explains, the bass shows proper voice leading, moving in
          seconds: down a major second, then a minor second, a minor second and a minor second.
          You hear the same device in "Michelle" by the Beatles.
        </p>
      </SISCard>

      <RomanNumeralAnalysis
        label="The mediant move — rising minor 9th chords (E major)"
        caption="Jason calls this a mediant modulation — a move to the chord on the 3rd degree, G♯ minor, here coloured as G♯m9. Then the bass rises G♯ → A → B (a half step, then a whole step), all minor chords, before Aadd2 and A/C♯. Jason names all three as minor 9ths in the lesson; his chart writes the second and third as Am7 and Bm7."
        chords={[
          { chord: "G♯m9", rn: "iii9", function: "Mediant",  pitches: ["G#3", "B3",  "D#4", "F#4", "A#4"] },
          { chord: "Am9",  rn: "iv9",  function: "Borrowed", pitches: ["A3",  "C4",  "E4",  "G4",  "B4"]  },
          { chord: "Bm9",  rn: "v9",   function: "Borrowed", pitches: ["B3",  "D4",  "F#4", "A4",  "C#5"] },
        ]}
      />

      <SISCard label="Carnatic context" title="7/8 and Misra Chapu tala — seven-beat time in Indian tradition">
        <p>
          Western pop treats seven-beat meters as unusual — Pink Floyd's "Money" and Dave
          Brubeck's "Unsquare Dance" (both mostly in 7/4) are the famous examples. But in Carnatic
          music, 7 is <strong>Misra Chapu tala</strong> — one of the standard rhythmic cycles,
          grouped 3+2+2 (or 3+4). Carnatic musicians are taught to feel these groupings from
          early on.
        </p>
        <p>
          So a seven-beat song in Tamil film music is less strange than it might look on paper.
          It is mainly for pianists trained only in Western pop that 7 feels unusual. This lesson
          is your passport into a wider rhythmic world.
        </p>
      </SISCard>

      <SISCallout label="Practise this — the tap-and-hum method">
        Before touching the piano: tap the 7/8 cycle on your knee as 4+3, the way Jason's chart
        beams it. Accent beats 1 and 5: TAP-tap-tap-tap-TAP-tap-tap. Loop this for one minute,
        then try it as 3+2+2 (accents on 1, 4 and 6) and notice the difference. Now hum the melody of
        Kaatrai Konjam over your tapping. Feel where the melody lands on the accented beats and
        where it floats across them. Only once the rhythm lives in your body should you sit at
        the keyboard.
      </SISCallout>

      <SISInfluence artists={["Dave Brubeck Quartet (Take Five, Unsquare Dance)", "Carnatic Misra Chapu percussion", "Gospel choirs"]}>
        <p>
          Dave Brubeck's "Take Five" (5/4) and "Unsquare Dance" (7/4) showed Western jazz
          audiences that odd meters could swing. Here the gospel chord vocabulary Jason points
          out — IV → I, a line cliché, ninth chords — gives the 7/8 cycle warmth and
          accessibility. Listen to Brubeck, to mridangam playing in Misra Chapu, and to gospel
          choirs, then come back to Kaatrai Konjam. Jason also notes that Ilaiyaraaja is,
          <em> "deep down,"</em> a bass guitarist — <em>"so he knows how to think groove."</em>
        </p>
      </SISInfluence>

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/04-kaatrai-konjam-page1.png"
        caption="Jason's chart — the 7/8 groove counted '1 & 2 & … 7 &' with its hits circled, a 7/8 bar beamed 4 + 3, and the melody notes written under the counts."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/04-kaatrai-konjam-page2.png"
        caption="Page 2 — the E major scale with its sargam (S R G M P D N), the I, IV and V chords (E, A, B) with their voicings, and a 7/8 bar counted 1 2 3 4 | 5 6 7."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/04-kaatrai-konjam-page3.png"
        caption="Page 3 — the line cliché at 1:17 in the song: E · Bm/D · Amaj7/C♯ · Am/C · E/B · B, counted across the seven beats."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/04-kaatrai-konjam-page4.png"
        caption="Page 4 — more of the chart: E · F♯m7 · E; G♯m9 (G♯m7 plus A♯) · Am7 Bm7 · Aadd2 · A/C♯; G♯m7 · Amaj6 · C♯m · B · E."
      />

      <SISTakeaway>
        Seven is not harder than four — it is just unfamiliar. The way to make it familiar is
        not to count mechanically but to find the internal groupings (4+3 or 3+2+2) and let
        them become physical. Every time you expand your metric vocabulary, you expand what you
        can compose and what you can hear. After this lesson, "Take Five" and "Unsquare Dance"
        will feel different. So will every song with a slightly stumbling, floating feel — and
        you will start to wonder: is this 7?
      </SISTakeaway>
    </LessonShell>
  );
}
